import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
} from "antd";
import Table from "../../../../Componets/Table";
import Header from "../../../../Componets/Header";
import {
  EyeOutlined,
  SyncOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import {
  auto_reconcile,
  delete_bank_statement,
  get_bank_statements,
} from "../../../../Controllers/Accounting/BankReconcilliation";
import { get_all_accounts } from "../../../../Controllers/Accounting/Services/chartOfAccountsService";
import tables from "../../../../Data/Tables/Accounting/BankReconcilliation/tables";
import headers from "../../../../Data/Tables/Accounting/BankReconcilliation/headers";
const { RangePicker } = DatePicker;

const BankStatementList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({ status: null, bankAccountId: null });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [header, setHeader] = useState(headers);

  useEffect(() => {
    fetchStatements();
    fetchBankAccounts();
  }, [pagination.current, filters]);

  const fetchStatements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user");
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status,
        bankAccountId: filters.bankAccountId,
      };
      const response = await get_bank_statements(params);

      if (response.data) {
        let data = [];

        response?.data?.statements?.map((value, index) => {
          data?.push({
            "Statement ID": value.statementId,
            "Bank Account": value?.bankAccountId?.name,
            Period:
              value.statementPeriodStart && value.statementPeriodEnd
                ? `${moment(value.statementPeriodStart).format("DD/MM/YYYY")} - ${moment(value.statementPeriodEnd).format("DD/MM/YYYY")}`
                : "N/A",
            "Opening Balance": value?.openingBalance,
            "Closing Balance": value?.closingBalance,
            Reconciled: value.transactions?.length
              ? `${value.summary?.reconciledCount || 0} / ${value.transactions.length}`
              : "No Transactions",
            Status: getStatusTag(value.status),
            Uploaded: moment(value.createdAt).format("DD/MM/YYYY HH:mm"),
            Actions: (
              <Space>
                <Tooltip title="View Statement">
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() =>
                      navigate(
                        `/app/bank-reconciliation/statement/${value._id}`,
                      )
                    }
                  />
                </Tooltip>
                <Tooltip title="Auto Reconcile">
                  <Button
                    icon={<SyncOutlined />}
                    size="small"
                    onClick={() => handleAutoReconcile(value._id)}
                    disabled={value.status === "completed"}
                  />
                </Tooltip>
                <Tooltip title="Generate Report">
                  <Button
                    icon={<FileTextOutlined />}
                    size="small"
                    onClick={() =>
                      navigate(`/app/bank-reconciliation/report/${value._id}`)
                    }
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    onClick={() => handleDelete(value._id)}
                  />
                </Tooltip>
              </Space>
            ),
          });
        });

        setStatements(data);
        setPagination({
          currentPage: response?.data?.meta?.currentPage,
          totalCount: response?.data?.meta?.totalCount,
          totalPages: response?.data?.meta?.totalPages,
        });

        const stats = response.data.statements;
        setSummary({
          total: stats.length,
          completed: stats.filter((s) => s.status === "completed").length,
          inProgress: stats.filter((s) => s.status === "in_progress").length,
          pending: stats.filter((s) => s.status === "pending").length,
          totalAmount: stats.reduce(
            (sum, s) => sum + (s.closingBalance || 0),
            0,
          ),
        });
      }
    } catch (error) {
      toast.error("Failed to fetch statements");
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem("user");
      const body = { type: "Bank", category: "Assets", status: 1 };
      const response = await get_all_accounts(body);

      if (response.data) {
        setBankAccounts(response.data);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this bank statement?"))
      return;

    try {
      const token = localStorage.getItem("user");
      await delete_bank_statement(id);
      toast.success("Bank statement deleted successfully");
      fetchStatements();
    } catch (error) {
      toast.error("Failed to delete statement");
    }
  };

  const handleAutoReconcile = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user");
      const response = await auto_reconcile(id);

      if (response.data?.success) {
        toast.success(
          `Auto-reconciliation completed: ${response.data.data.matchedCount} matched, ${response.data.data.unmatchedCount} unmatched`,
        );
        fetchStatements();
      }
    } catch (error) {
      toast.error("Auto-reconciliation failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: "default", text: "Pending" },
      in_progress: { color: "processing", text: "In Progress" },
      completed: { color: "success", text: "Completed" },
      cancelled: { color: "error", text: "Cancelled" },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const paginationdata = (data) => {
    let headerdata = {};

    header?.components?.map((value, index) => {
      headerdata[value.name] = value.value;
    });

    fetchStatements({
      ...data,
    });
  };

  return (
    <Header headers={header} setHeaders={setHeader}>
      <div className="mb-2 flex justify-between items-center mt-2">
        <div></div>
        <Button
          type="primary"
          onClick={() => navigate("/app/bank-reconciliation/upload")}
        >
          Upload Statement
        </Button>
      </div>

      {summary && (
        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card>
              <Statistic title="Total Statements" value={summary.total} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Completed"
                value={summary.completed}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="In Progress"
                value={summary.inProgress}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pending"
                value={summary.pending}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <div className="mb-4 flex gap-4">
          <Select
            placeholder="Filter by status"
            allowClear
            style={{ width: 200 }}
            onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))}
          >
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="in_progress">In Progress</Select.Option>
            <Select.Option value="completed">Completed</Select.Option>
          </Select>
          <Select
            placeholder="Filter by bank account"
            allowClear
            style={{ width: 250 }}
            showSearch
            onChange={(val) =>
              setFilters((prev) => ({ ...prev, bankAccountId: val }))
            }
          >
            {bankAccounts.map((acc) => (
              <Select.Option key={acc._id} value={acc._id}>
                {acc.name} ({acc.code})
              </Select.Option>
            ))}
          </Select>
          <Button onClick={fetchStatements}>Refresh</Button>
        </div>

        <Table
          head={tables}
          body={statements}
          pagination={pagination}
          paginationdata={paginationdata}
          summary={1}
        />
      </Card>
    </Header>
  );
};

export default BankStatementList;
